/**
 * Early keys: the keyboard layer loads beside the first route (it is not part of the first-paint
 * bundle), so a chord pressed in that first moment — ⌘K right after the page appears — would be lost.
 * `captureEarlyKeys()` (dependency-free, imported by the app root) remembers chords and `?` until the
 * layer attaches; `InteractionRoot` then replays them once. Plain letters are never replayed.
 */
const buffer: KeyboardEvent[] = [];
let detach: (() => void) | null = null;
let done = false;

/** Start remembering chords (idempotent; a no-op once the keyboard layer took over). */
export function captureEarlyKeys(): void {
  if (detach || done || typeof window === 'undefined') return;
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.isComposing || event.repeat) return;
    if (!(event.metaKey || event.ctrlKey || event.altKey || event.key === '?')) return;
    buffer.push(event);
    if (buffer.length > 4) buffer.shift();
  };
  window.addEventListener('keydown', onKeyDown);
  detach = () => window.removeEventListener('keydown', onKeyDown);
}

/** Stop capturing and return the chords of the last `maxAgeMs` that nothing handled. */
export function takeEarlyKeys(
  maxAgeMs = 4000,
  now: number = typeof performance !== 'undefined' ? performance.now() : 0,
): KeyboardEvent[] {
  detach?.();
  detach = null;
  done = true;
  const out = buffer.filter((e) => !e.defaultPrevented && now - e.timeStamp <= maxAgeMs);
  buffer.length = 0;
  return out;
}
